import React from 'react';
import PropTypes from 'prop-types';
import { Scrollbars } from 'react-custom-scrollbars';
import Waypoint from 'react-waypoint';
import debounce from 'lodash/debounce';

import Snoozes from 'api/resources/snoozes';
import getErrors from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';

import Snooze from 'admin-editor/components/Snooze';
import SuggestedSnoozeItem from 'admin-editor/components/SuggestedSnoozeItem';

import Popup from 'components/Popup';
import Loader from 'components/Loader';

import ErrorText from 'styled/Page/ErrorText';
import Input from 'styled/Input';
import SimpleList from 'styled/SimpleList';
import DropdownList from 'styled/DropdownList';

const PAGE_SIZE = 20;
const VISIBLE_SNOOZES_NUMBER = 8;

class SnoozesAutocomplete extends React.PureComponent {
  state = {
    suggestions: [],
    query: '',
    isOpen: false,
    errors: [],
    hasMore: false,
    pageNumber: 0,
    snoozes: this.props.snoozes,
  }

  loadSnoozesWithDebounce = debounce(() => {
    this.reloadSnoozes();
  }, 500);

  handleInputChange = (e) => {
    this.setState({ query: e.target.value }, this.loadSnoozesWithDebounce);
  }

  handleInputFocus = () => {
    this.setState({ isOpen: true }, this.reloadSnoozes);
  }

  reloadSnoozes = () => {
    this.setState({
      pageNumber: 0,
      suggestions: [],
      hasMore: false,
    }, this.loadSnoozes);
  }

  handleSubmit = (e) => {
    e.preventDefault();
    this.reloadSnoozes();
  }

  handleOnBackdropClick = () => {
    this.setState({ isOpen: false });
  }

  handleSuggestionClick = (snooze) => {
    this.setState(prevState => ({
      suggestions: prevState.suggestions.filter(item => item.id !== snooze.id),
      snoozes: prevState.snoozes.concat(snooze),
      query: '',
    }), this.updateSnoozes);

    if (this.state.suggestions.length <= VISIBLE_SNOOZES_NUMBER && this.state.hasMore) {
      this.loadSnoozes();
    }
  }

  handleSnoozeDelete = (snooze) => {
    this.setState(prevState => ({
      snoozes: prevState.snoozes.filter(item => item.id !== snooze.id),
    }), this.updateSnoozes);
  }

  updateSnoozes = () => {
    this.props.onChange(this.state.snoozes);
  }

  loadSnoozes = async () => {
    this.setState({
      loading: true,
      errors: [],
    });

    try {
      const response = await Snoozes.getAll({
        page: {
          number: this.state.pageNumber,
          size: PAGE_SIZE,
        },
        filter: {
          q: this.state.query,
        },
      });

      const suggestions = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);
      const filteredSuggestion = suggestions.filter((suggestion) => {
        const alreadySelected = this.state.snoozes.some(snooze => snooze.id === suggestion.id);
        if (alreadySelected) return false;

        return true;
      });

      this.setState(prevState => ({
        suggestions: prevState.suggestions.concat(filteredSuggestion),
        loading: false,
        hasMore: suggestions.length === PAGE_SIZE,
        pageNumber: prevState.pageNumber + 1,
      }));
    } catch (err) {
      getErrors(err).then((errors) => {
        this.setState({
          errors,
          loading: false,
        });
      });
    }
  }

  render() {
    return (
      <React.Fragment>
        {this.state.snoozes.length > 0 && (
          <SimpleList>
            {this.state.snoozes.map(snooze => (
              <Snooze
                key={snooze.id}
                snooze={snooze}
                onDelete={this.handleSnoozeDelete}
                disabled={this.props.disabled}
              />
            ))}
          </SimpleList>
        )}

        <form onSubmit={this.handleSubmit}>
          <Input
            value={this.state.query}
            onChange={this.handleInputChange}
            onFocus={this.handleInputFocus}
            placeholder="Введите снуз"
            disabled={this.props.disabled}
          />
        </form>

        <div style={{ position: 'relative' }}>
          <Popup
            isVisible={this.state.isOpen}
            onClickOnBackdrop={this.handleOnBackdropClick}
            isControlled
          >
            <DropdownList style={{ width: '319px' }}>
              <Scrollbars
                autoHide
                autoHeight
                autoHeightMax="250px"
              >
                {this.state.suggestions.map(suggestion => (
                  <SuggestedSnoozeItem
                    key={suggestion.id}
                    onClick={this.handleSuggestionClick}
                    suggestion={suggestion}
                  />
                ))}

                {this.state.hasMore &&
                  <Waypoint onEnter={this.loadSnoozes} />
                }

                {this.state.suggestions.length === 0 && !this.state.loading && (
                  <ErrorText role="alert" style={{ textAlign: 'center' }}>
                    Нет доступных для выбора снузов
                  </ErrorText>
                )}

                {this.state.loading && <Loader />}

                {this.state.errors.map(error => (
                  <ErrorText role="alert" key={error}>{error}</ErrorText>
                ))}
              </Scrollbars>
            </DropdownList>
          </Popup>
        </div>
      </React.Fragment>
    );
  }
}

SnoozesAutocomplete.propTypes = {
  onChange: PropTypes.func,
  snoozes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),
  disabled: PropTypes.bool,
};

SnoozesAutocomplete.defaultProps = {
  onChange: () => {},
  snoozes: [],
  disabled: false,
};

export default SnoozesAutocomplete;
